import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import { RegisterOptions } from 'react-hook-form';

import { RootState, AppThunkConfig } from '../../app/store';
import { generatePath, route_path } from '../../app/route';
import { Role } from '../../types';
import { bindAPI } from '../../app/auth';
import {
  ListRolesParams,
  ListRolesResponse,
  listRoles,
  GetRoleResponse,
  getRole,
  CreateRoleParams,
  CreateRoleResponse,
  createRole,
  UpdateRoleParams,
  UpdateRoleResponse,
  updateRole,
  DeleteRoleParams,
  DeleteRoleResponse,
  deleteRole,
} from '../../handlers';
import { logoutAction, hasScope } from '../auth/authSlice';
import {
  setCurrentTenantId,
  selectCurrentTenantId,
} from '../user/userSlice';
import {
  stripEmptyString,
} from '../../utils';

export type RoleState = {
  roles: Omit<Role, 'scopes'>[];
  list_status: 'idle'|'loading'|'failed'|'success';
  error?: SerializedError;
  role?: Role;
}

const initialState: RoleState = {
  roles: [],
  list_status: 'idle',
};

export type ListRolesActionParams = ListRolesParams;
export type ListRolesActionResponse = ListRolesResponse;
export const listRolesAction = createAsyncThunk<
  ListRolesActionResponse, ListRolesActionParams, AppThunkConfig
>(
  'role/list',
  (params, thunkAPI) => bindAPI(listRoles, thunkAPI)(params),
);

export type GetRoleActionParams = {
  tenant_id: string;
  role_id: string;
};
export type GetRoleActionResponse = GetRoleResponse;
export const getRoleAction = createAsyncThunk<
  GetRoleActionResponse, GetRoleActionParams, AppThunkConfig
>(
  'role/get',
  async({ tenant_id, role_id }, thunkAPI) => {
    const [role_tenant_id, role_name] = role_id.split('/');
    let res_data = await bindAPI(getRole, thunkAPI)({
      tenant_id: role_tenant_id,
      name: role_name,
    });
    return res_data;
  }
);

export type CreateRoleActionParams = CreateRoleParams;
export type CreateRoleActionResponse = CreateRoleResponse;
export const createRoleAction = createAsyncThunk<
  CreateRoleActionResponse, CreateRoleActionParams, AppThunkConfig
>(
  'role/create',
  async({ body, ...args }, thunkAPI) => {
    return bindAPI(createRole, thunkAPI)({
      ...args,
      body: stripEmptyString(body)
    });
  }
);

export type UpdateRoleActionParams = UpdateRoleParams;
export type UpdateRoleActionResponse = UpdateRoleResponse;
export const updateRoleAction = createAsyncThunk<
  UpdateRoleActionResponse, UpdateRoleActionParams, AppThunkConfig
>(
  'role/update',
  async({ body, ...args }, thunkAPI) => {
    let res_data = await bindAPI(updateRole, thunkAPI)({
      ...args,
      body: stripEmptyString(body, { convert_to_null: true }),
    });
    return res_data;
  }
);

export type DeleteRoleActionParams = DeleteRoleParams;
export type DeleteRoleActionResponse = DeleteRoleResponse;
export const deleteRoleAction = createAsyncThunk<
  DeleteRoleActionResponse, DeleteRoleActionParams, AppThunkConfig
>(
  'role/delete',
  async(params: DeleteRoleParams, thunkAPI) => {
    let res_data = await bindAPI(deleteRole, thunkAPI)(params);
    return res_data;
  }
);

export const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listRolesAction.pending, (state) => {
        state.list_status = 'loading';
      })
      .addCase(listRolesAction.fulfilled, (state, action) => {
        state.list_status = 'success';
        state.roles = action.payload.data;
      })
      .addCase(listRolesAction.rejected, (state, action) => {
        state.list_status = 'failed';
        state.error = action.error;
      })
      .addCase(getRoleAction.fulfilled, (state, action) => {
        state.role = action.payload;
      })
      .addCase(getRoleAction.rejected, (state, action) => {
        state.error = action.error;
      })
      .addCase(logoutAction, (state) => initialState)
      .addCase(LOCATION_CHANGE, (state) => initialState)
      .addCase(setCurrentTenantId, (state) => initialState);
  },
});

export const selectListRoleAPIStatus = (state: RootState) => state.role.list_status;
export const selectRoles = (state: RootState) => state.role.roles;

export const selectRoleDetail = (state: RootState) => state.role.role;
export const canActionByRole = (
  role: Pick<Role, 'type'|'state'>,
  action: 'edit'|'view'|'enable'|'disable'|'delete',
  tenant_id: string,
) => {
  if (action === 'edit') {
    return role.type !== 'owner';
  } else if (action === 'view') {
    return true;
  } else if (action === 'enable') {
    return role.state === 'disabled';
  } else if (action === 'disable') {
    return role.type !== 'owner' && role.state === 'enabled';
  } else if (action === 'delete') {
    return role.type !== 'owner';
  }
  return false;
};

export const selectCanListRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.list');
}
export const selectCanEditRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.update') &&
    hasScope(state, 'auth-portal/user_roles.get');
}
export const selectCanViewRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.get');
}
export const selectCanEnableRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.update');
}
export const selectCanDisableRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.update');
}
export const selectCanDeleteRole = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_roles.delete');
}
export const selectRolesAndActions = (state: RootState) => {
  const tenant_id = selectCurrentTenantId(state);
  const roles = selectRoles(state);
  return roles.map(role => {
    return {
      ...role,
      $action: {
        can_edit: selectCanEditRole(state) && canActionByRole(role, 'edit', tenant_id),
        can_view: selectCanViewRole(state) && canActionByRole(role, 'view', tenant_id),
        can_enable: selectCanEnableRole(state) && canActionByRole(role, 'enable', tenant_id),
        can_disable: selectCanDisableRole(state) && canActionByRole(role, 'disable', tenant_id),
        can_delete: selectCanDeleteRole(state) && canActionByRole(role, 'delete', tenant_id),
      },
    };
  });
};

export const serializeRoleId = (role_id: string) => {
  return role_id.replace('/', ':');
}

export const deserializeRoleId = (role_id: string) => {
  return role_id.replace(':', '/');
}

export const getRoleCreateUrl = (tenant_id: string) => generatePath(route_path.role_detail, {
  tenant_id, mode: 'create',
});
export const getRoleListUrl = (tenant_id: string) => generatePath(route_path.role_list, { tenant_id });
export const getRoleDetailUrl = (tenant_id: string, role_id: string, mode: 'edit'|'view') => {
  return generatePath(route_path.role_detail, {
    tenant_id, mode,
  })+`?role_id=${serializeRoleId(role_id)}`;
};

export const role_field_options: Partial<Record<keyof Role, RegisterOptions>> = {
  title: {
    required: 'Required',
    minLength: {
      value: 2,
      message: 'Length must be at least 4 characters long',
    },
    maxLength: {
      value: 32,
      message: 'length must be less than or equal to 32 characters long',
    },
  },
  name: { 
    required: 'Required',
    minLength: {
      value: 2,
      message: 'Length must be at least 2 characters long',
    },
    maxLength: {
      value: 32,
      message: 'Length must be less than or equal to 32 characters long',
    },
    pattern: {
      value: /^[a-z][a-z_.0-9]*$/,
      message: 'Fails to match the required pattern: /^[a-z][a-z_.0-9]*$/',
    },
  },
  description: { 
    maxLength: {
      value: 2048,
      message: 'Length must be less than or equal to 2048 characters long',
    },
  },
};
